import React, { useRef, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { SessionGoalDialog } from "./therapy/SessionGoalDialog";
import { RMethodTracker } from "./therapy/RMethodTracker";
import { SessionSummaryDialog } from "./therapy/SessionSummaryDialog";
import { RecommendedTools } from "./therapy/RecommendedTools";
import { VoiceModeInfo } from "./therapy/VoiceModeInfo";
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";
import { useTherapyChat, ChatHeader, ChatMessages, ChatInput } from "./therapy/chat";

interface TherapyChatProps {
  user: User;
  selectedMode?: string;
  onModeChange?: (mode: string) => void;
  initialContext?: string;
}

export const TherapyChat: React.FC<TherapyChatProps> = ({ 
  user, 
  selectedMode = 'general', 
  onModeChange, 
  initialContext 
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [showGoalDialog, setShowGoalDialog] = useState(true);
  const [showVoiceInfo, setShowVoiceInfo] = useState(false);
  const [hasSeenVoiceInfo, setHasSeenVoiceInfo] = useState(() => {
    return localStorage.getItem('therappi_seen_voice_info') === 'true';
  });

  const {
    messages,
    isLoading,
    isStreaming,
    conversationId,
    sessionGoal,
    rMethodStep,
    sessionSummary,
    showSummaryDialog,
    setShowSummaryDialog,
    recommendedTools,
    voiceMode,
    totalCredits,
    handleSendMessage,
    handleDeleteMessage,
    handleDeleteConversation,
    handleSetGoal,
    handleSaveInsight,
    handleGenerateSummary,
    handleToolClick,
    toggleVoiceMode
  } = useTherapyChat({
    userId: user.id,
    selectedMode,
    initialContext
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Show voice info dialog on first visit
  useEffect(() => {
    if (!hasSeenVoiceInfo && messages.length >= 4 && !isLoading) {
      setShowVoiceInfo(true);
      localStorage.setItem('therappi_seen_voice_info', 'true');
      setHasSeenVoiceInfo(true);
    }
  }, [messages.length, hasSeenVoiceInfo, isLoading]);

  const onSetGoal = (goal: string) => {
    handleSetGoal(goal);
    setShowGoalDialog(false);
  };

  return (
    <div className="flex flex-col h-full">
      <ChatHeader 
        selectedMode={selectedMode}
        messagesLength={messages.length}
        conversationId={conversationId}
        onModeChange={onModeChange}
        onGenerateSummary={handleGenerateSummary}
        onDeleteConversation={handleDeleteConversation}
      />

      {/* R-Method Tracker for QSP Mode */}
      {selectedMode === 'qsp' && messages.length > 2 && (
        <div className="mx-6 mb-4">
          <RMethodTracker 
            currentStep={rMethodStep}
            steps={['Recognise', 'Reflect', 'Release', 'Reprogramme']}
          />
        </div>
      )}

      {/* Recommended Tools */}
      {recommendedTools.length > 0 && (
        <div className="mx-6 mb-4">
          <RecommendedTools 
            tools={recommendedTools}
            onToolClick={handleToolClick}
          />
        </div>
      )}

      <ChatMessages 
        messages={messages}
        isLoading={isLoading}
        messagesEndRef={messagesEndRef}
        onDeleteMessage={handleDeleteMessage}
        onSaveInsight={handleSaveInsight}
      />

      <ChatInput 
        totalCredits={totalCredits}
        voiceMode={voiceMode}
        isStreaming={isStreaming}
        onSendMessage={handleSendMessage}
        onToggleVoiceMode={toggleVoiceMode}
      />

      {/* Session Goal Dialog */}
      <SessionGoalDialog 
        isOpen={showGoalDialog && messages.length <= 2}
        onClose={() => setShowGoalDialog(false)}
        onSetGoal={onSetGoal}
        mode={selectedMode}
      />

      {/* Session Summary Dialog */}
      {sessionSummary && (
        <SessionSummaryDialog 
          isOpen={showSummaryDialog}
          onClose={() => setShowSummaryDialog(false)}
          summary={sessionSummary}
        />
      )}

      {/* Voice Mode Info Dialog */}
      <VoiceModeInfo 
        isOpen={showVoiceInfo}
        onClose={() => setShowVoiceInfo(false)}
      />

      {/* Contextual notification prompt */}
      {messages.length > 3 && <ContextualNotificationPrompt context="therapy" className="mt-4" />}
    </div>
  );
};
